import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Link2, Download, Smartphone, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import { trackEvent } from "@/lib/analytics";

const BASE = process.env.REACT_APP_BACKEND_URL;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" /></svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M18.2 2.3h3.4l-7.4 8.4 8.7 11.5h-6.8l-5.3-7-6.1 7H1.3l7.9-9L.9 2.3h7l4.8 6.4 5.5-6.4zm-1.2 17.9h1.9L7.1 4.2H5.1l11.9 16z" /></svg>
);

export function ShareButton({ article, variant = "icon", kind = "article" }) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const openIt = (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); };
  return (
    <>
      {variant === "icon" ? (
        <button data-testid={kind === "x" ? "x-pulse-share-btn" : "share-icon-btn"} onClick={openIt} aria-label={t("share.title")} className={`inline-flex w-8 h-8 items-center justify-center rounded-full transition-colors ${kind === "x" ? "text-slate-400 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-violet-700 hover:bg-violet-50"}`}>
          <Share2 size={15} />
        </button>
      ) : (
        <button data-testid="share-story-btn" onClick={openIt} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-semibold shadow-[0_8px_24px_rgba(124,58,237,0.35)] hover:shadow-[0_10px_30px_rgba(124,58,237,0.5)] hover:-translate-y-0.5 transition-[transform,box-shadow]">
          <Share2 size={16} /> {t("share.button")}
        </button>
      )}
      {open && <ShareDialog article={article} kind={kind} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareDialog({ article, kind, onClose }) {
  const { t, lang, title } = useI18n();
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const prefix = kind === "x" ? "share/x" : "share";
  const shareUrl = `${BASE}/api/${prefix}/${article.id}?lang=${lang}`;
  const cardUrl = `${BASE}/api/${prefix}/card/${article.id}.png?lang=${lang}`;
  const text = `${title(article)} — NxtTechNews`;
  const fileName = `nxttechnews-${article.id.slice(0, 8)}.png`;

  const getFile = async () => {
    const blob = await (await fetch(cardUrl)).blob();
    return new File([blob], fileName, { type: "image/png" });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = Object.assign(document.createElement("textarea"), { value: shareUrl });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(t("share.copied"));
  };
  const download = async () => {
    setBusy(true);
    try {
      const f = await getFile();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(f);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally { setBusy(false); }
  };
  const nativeShare = async () => {
    setBusy(true);
    try {
      const f = await getFile();
      if (navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], text: `${text}\n${shareUrl}` });
      else await navigator.share({ title: text, url: shareUrl });
    } catch { /* user cancelled */ } finally { setBusy(false); }
  };
  const ev = (method) => trackEvent("share", { method, content_type: kind === "x" ? "x_post" : "article", item_id: article.id });
  const btn = "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-dialog" className="max-w-xl p-0 overflow-hidden border-violet-500/30">
        <div className="p-5 pb-0">
          <DialogTitle className="font-display text-lg">{t("share.title")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">{t("share.desc")}</DialogDescription>
        </div>
        <div className="px-5 pt-4">
          <div className="relative aspect-[1200/630] rounded-xl overflow-hidden bg-[#0b0d13] ring-1 ring-violet-500/30">
            {!loaded && <span className="absolute inset-0 flex items-center justify-center text-violet-300"><Loader2 className="animate-spin" /></span>}
            <img data-testid="share-card-preview" src={cardUrl} alt={title(article)} onLoad={() => setLoaded(true)} className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`} />
          </div>
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <a data-testid="share-whatsapp" onClick={() => ev("whatsapp")} href={`https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className={`${btn} bg-[#25D366] text-white hover:bg-[#1ebe5b]`}><WhatsAppIcon /> WhatsApp</a>
          <a data-testid="share-x" onClick={() => ev("x")} href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className={`${btn} bg-black text-white hover:bg-slate-800 ring-1 ring-white/10`}><XIcon /> X</a>
          <button data-testid="share-copy" onClick={() => { ev("copy_link"); copy(); }} className={`${btn} bg-violet-50 text-violet-700 hover:bg-violet-100`}><Link2 size={16} /> {t("share.copy")}</button>
          <button data-testid="share-download" onClick={() => { ev("download"); download(); }} disabled={busy} className={`${btn} bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-60`}><Download size={16} /> {t("share.download")}</button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button data-testid="share-native" onClick={() => { ev("native"); nativeShare(); }} disabled={busy} className={`${btn} col-span-2 sm:col-span-4 bg-gradient-to-r from-violet-600 to-cyan-500 text-white disabled:opacity-60`}><Smartphone size={16} /> {t("share.native")}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
